import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Menu, X, User, LogOut, LayoutDashboard, ShieldCheck } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_findmed-app/artifacts/ge9othd8_LOGO%20SOS%20FINDMED.png";

export default function Header() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <img src={LOGO_URL} alt="SOS FindMed" className="h-14 sm:h-16 w-auto object-contain" style={{ filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.9)) drop-shadow(0 0 12px rgba(255,255,255,0.4))' }} />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-[#5C6666] hover:text-[#1A3636] transition-colors font-medium">
              Inicio
            </Link>
            {user ? (
              <>
                <Link 
                  to="/dashboard" 
                  className="text-[#5C6666] hover:text-[#1A3636] transition-colors font-medium flex items-center gap-2"
                  data-testid="nav-dashboard"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Mi Panel
                </Link>
                {isAdmin && (
                  <Link 
                    to="/admin" 
                    className="text-[#5C6666] hover:text-[#1A3636] transition-colors font-medium flex items-center gap-2"
                    data-testid="nav-admin"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    Admin
                  </Link>
                )}
                <div className="flex items-center gap-3 pl-4 border-l border-[#E5E7EB]">
                  <span className="text-sm text-[#5C6666]">{user.name}</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleLogout}
                    data-testid="logout-button"
                    className="text-[#C81D25] hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="text-[#5C6666] hover:text-[#1A3636] transition-colors font-medium"
                  data-testid="nav-login"
                >
                  Iniciar Sesión
                </Link>
                <Button 
                  asChild 
                  className="bg-[#1A3636] hover:bg-[#0D1111] text-white"
                  data-testid="nav-register"
                >
                  <Link to="/register">Registrarse</Link>
                </Button>
              </>
            )}
          </nav>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-[#1A3636]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#E5E7EB]">
            <nav className="flex flex-col gap-3">
              <Link 
                to="/" 
                className="px-4 py-2 text-[#5C6666] hover:bg-[#E8EDE7] rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Inicio
              </Link>
              {user ? (
                <>
                  <Link 
                    to="/dashboard" 
                    className="px-4 py-2 text-[#5C6666] hover:bg-[#E8EDE7] rounded-lg flex items-center gap-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Mi Panel
                  </Link>
                  {isAdmin && (
                    <Link 
                      to="/admin" 
                      className="px-4 py-2 text-[#5C6666] hover:bg-[#E8EDE7] rounded-lg flex items-center gap-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <ShieldCheck className="w-4 h-4" />
                      Admin
                    </Link>
                  )}
                  <div className="px-4 py-2 flex items-center justify-between border-t border-[#E5E7EB] mt-2 pt-4">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-[#5C6666]" />
                      <span className="text-sm text-[#5C6666]">{user.name}</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      className="text-[#C81D25]"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Salir
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="px-4 py-2 text-[#5C6666] hover:bg-[#E8EDE7] rounded-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Iniciar Sesión
                  </Link>
                  <Link 
                    to="/register" 
                    className="mx-4 py-2 text-center bg-[#1A3636] text-white rounded-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Registrarse
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
